import React, { useState, useEffect, useRef } from 'react';
import { Icon, Dropdown } from 'semantic-ui-react';
import { Aggregation, Aggregate, FilterData, DynamicSearchResponse } from '../../../types/filterDataGridDTO';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import _ from 'lodash';
import './DataGridFilters.css';
import moment from 'moment';
import { debounce } from 'lodash';
import AxiosInstance from '../../../helpers/axiosInstance';
import iconDownArraow from '../../../assets/images/ico-arrow-down.svg';

interface DataGridWithFiltersProps<T> {
  aggregations: Aggregation[] | null;
  filters: Record<string, FilterData> | null;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, FilterData> | null>>;
  responseDataModel: DynamicSearchResponse<T> | null;
  setResponseDataModel: React.Dispatch<React.SetStateAction<DynamicSearchResponse<T> | null>>;
  defaultFilterExpanded?: Record<string, boolean>;
}

interface CheckboxGroupProps {
  aggregation: Aggregation;
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

interface DateRangeGroupProps {
  aggregation: Aggregation;
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

interface MultiSelectGroupProps {
  aggregation: Aggregation;
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const DataGridFilters = <T,>({
  aggregations,
  filters,
  setFilters,
  responseDataModel,
  setResponseDataModel,
  defaultFilterExpanded
}: DataGridWithFiltersProps<T>) => {

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (defaultFilterExpanded) {
      setExpandedSections(defaultFilterExpanded);
    }
  }, [])

  function firstLower(lower: string) {
    return lower && lower[0].toLowerCase() + lower.slice(1) || lower;
  }

  const renderControl = (aggregation: Aggregation) => {
    const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ aggregation, expandedSections, setExpandedSections }) => {
      const [showMore, setShowMore] = useState(false);
      const maxItemsToShow = 5;

      const checkboxGroupRef = useRef<HTMLDivElement>(null);

      useEffect(() => {
        const container = checkboxGroupRef.current;
        if (!container) return;

        const handleLabelClick = (event: MouseEvent) => {
          event.stopPropagation();
        };

        const labels = container.querySelectorAll('label');
        labels.forEach((label) => {
          label.addEventListener('click', handleLabelClick);
        });

        return () => {
          labels.forEach((label) => {
            label.removeEventListener('click', handleLabelClick);
          });
        };
      }, [aggregation]);

      const toggleExpand = (name: string) => {
        setExpandedSections((prevExpandedSections) => ({
          ...prevExpandedSections,
          [name]: !prevExpandedSections[name],
        }));
      };

      const visibleItems = showMore
        ? aggregation.aggregates
        : aggregation.aggregates.slice(0, maxItemsToShow);

      const handleShowMoreToggle = () => {
        setShowMore((prevShowMore) => !prevShowMore);
      };

      const handleDropdownChange = (
        event: React.SyntheticEvent<HTMLElement>,
        data: any,
        aggregation: Aggregation
      ) => {
        const selectedValues = data.value as number[]; // New selected values from the dropdown
      
        setFilters((prevFilters) => {
          const updatedFilters = { ...prevFilters };
          const aggregationName = firstLower(aggregation.name);
      
          // Clear focus for all aggregations
          Object.keys(updatedFilters).forEach((key) => {
            updatedFilters[key].isFocus = false;
          });
      
          // Get the current filter data for the aggregation to retain other properties
          const currentFilterData = updatedFilters[aggregationName] || {
            fromDate: null,
            toDate: null,
            isFocusId: null,
            rowOrder: [],
            isNameFilter: false,
          };
      
          if (selectedValues.length === 0) {
            // Reset ids and names, while preserving other FilterData properties
            updatedFilters[aggregationName] = {
              ...currentFilterData,
              ids: [],
              names: [],
              isFocus: true,
            };
          } else {
            // Replace ids and names with the new values, retaining other properties
            const selectedNames = aggregation.aggregates
              .filter((item) => selectedValues.includes(item.id))
              .map((item) => item.name);
      
            updatedFilters[aggregationName] = {
              ...currentFilterData,
              ids: selectedValues,
              names: selectedNames,
              isFocus: true,
            };
          }
      
          return updatedFilters;
        });
      };      

      const handleCheckboxGroupChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        aggregation: Aggregation,
        item: Aggregate
      ) => {
        const { id, name } = item;

        setFilters((prevFilters) => {
          const updatedFilters = { ...prevFilters };
          const aggregationName = firstLower(aggregation.name);
          Object.keys(updatedFilters).map((key) => {
            updatedFilters[key].isFocus = false;
          });
          if (aggregation && !aggregation.isNameFilter && ((updatedFilters[aggregationName] || []).ids || []).includes(id)) {
            updatedFilters[aggregationName].ids = ((updatedFilters[aggregationName] || {}).ids || []).filter(
              (filterId: number) => filterId !== id
            );
            updatedFilters[aggregationName].names = ((updatedFilters[aggregationName] || {}).names || []).filter(
              (filterName: string) => filterName !== name
            );
          }
          else if (aggregation && aggregation.isNameFilter && ((updatedFilters[aggregationName] || []).names || []).includes(name)) {
            updatedFilters[aggregationName].ids = [];
            updatedFilters[aggregationName].names = ((updatedFilters[aggregationName] || {}).names || []).filter(
              (filterName: string) => filterName !== name
            );
          }
          else {
            if (aggregation && !aggregation.isNameFilter) {
              updatedFilters[aggregationName].ids = [...updatedFilters[aggregationName].ids, id];
            }
            updatedFilters[aggregationName].names = [...updatedFilters[aggregationName].names, name];
          }
          updatedFilters[aggregationName].isFocus = true;
          return updatedFilters;
        });
      };

      const checkBoxSelected = (aggregation: Aggregation, item: Aggregate) => {
        if (filters && aggregation.isNameFilter) {
          return ((filters[firstLower(aggregation.name)] || {}).names || []).includes(item.name);
        } else {
          return ((filters && filters[firstLower(aggregation.name)] || {}).ids || []).includes(item.id);
        }
      }

      const expanded = expandedSections[aggregation.name] || false;

      const searchableDropdownListLength = process.env.SEARCHABLE_DROPDOWN_LIST_LENGTH ? parseInt(process.env.SEARCHABLE_DROPDOWN_LIST_LENGTH) : 10;
      const isShowDropdown = (aggregation.aggregates.length > searchableDropdownListLength && aggregation.name !== 'Office') ? true : false;

      return (
        <div ref={checkboxGroupRef} className={`multi-select-group accordion__section ${expanded ? "is-showing show bg-white" : ""}`}>
          <button className="accordion__head" onClick={() => toggleExpand(aggregation.name)}>
            <h3>{aggregation.labelName}</h3>
            <div className="accordion__icon">
              <img src={iconDownArraow} alt="" />
            </div>
          </button>

          {isShowDropdown &&
            <div className='accordion__body'>
              <Dropdown
                placeholder={aggregation.labelName}
                fluid
                multiple
                search
                clearable
                selection
                options={_.map(aggregation.aggregates, (item, index) => ({
                  key: item.id,
                  text: `${item.name} (${item.count})`,
                  value: item.id,
                }))}
                value={filters && filters[firstLower(aggregation.name)]?.ids || []}
                onChange={(event, data) => handleDropdownChange(event, data, aggregation)}
                style={{ height: 'auto' }}
              />
            </div>
          }
          {!isShowDropdown &&
            <ul className="accordion__body">
              {visibleItems.map((item: Aggregate) => (
                <li key={item.id} className="checkbox-item">
                  <div className="checkbox">
                    <input
                      type="checkbox"
                      name={`field-${aggregation.name}-${item.id}#`}
                      id={`field-${aggregation.name}-${item.id}#`}
                      checked={checkBoxSelected(aggregation, item)}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleCheckboxGroupChange(event, aggregation, item);
                      }}
                    />
                    <span className="checkbox-custom"></span>
                    <label htmlFor={`field-${aggregation.name}-${item.id}#`} onClick={e => e.stopPropagation()}>
                      <span>{item.name}</span><span>({item.count})</span>
                    </label>
                  </div>

                </li>
              ))}
              {aggregation.aggregates.length > maxItemsToShow && (
                <li key={`Show more`} className="show-more cursor-pointer" onClick={handleShowMoreToggle}>
                  {showMore ? "Show less" : "Show more"}
                </li>
              )}
            </ul>
          }
        </div>
      );
    };

    const MultiSelectGroup: React.FC<MultiSelectGroupProps> = ({ aggregation, expandedSections, setExpandedSections }) => {
      const loadFromFilter = () => {
        const updatedSelectedItems = [] as Option[];
        ((filters || {})[firstLower(aggregation.name)].ids || []).map((id, index) => {
          const name = (filters || {})[firstLower(aggregation.name)].names[index];
          updatedSelectedItems.push({ id, name, count: 0 } as Option);
        });
        return updatedSelectedItems;
      }

      const [searchTerm, setSearchTerm] = useState('');
      const [isLoading, setIsLoading] = useState(false);
      const [items, setItems] = useState<Option[]>([]);
      const [selectedItems, setSelectedItems] = useState<Option[]>(loadFromFilter());
      const [displayItems, setDisplayItems] = useState<Option[]>([]);
      const [showMore, setShowMore] = useState(false);
      const [hasSearched, setHasSearched] = useState(false);
      const [searchedItems, setSearchedItems] = useState<Option[]>([]);
      const searchInputRef = useRef<HTMLInputElement>(null);

      const toggleExpand = (name: string) => {
        setExpandedSections((prevExpandedSections) => ({
          ...prevExpandedSections,
          [name]: !prevExpandedSections[name],
        }));
      };

      const fetchItems = debounce(async (searchTerm: string) => {
        if (searchTerm.length >= 3) {
          searchOptions(searchTerm);
        }
      }, 500);

      useEffect(() => {
        fetchItems(searchTerm);
      }, [searchTerm]);

      useEffect(() => {
        setDisplayItems(showMore ? searchedItems : searchedItems.slice(0, 5));
      }, [searchedItems, showMore]);

      const handleItemClick = (item: Option) => {
        try {
          const { id, name } = item;
          setSelectedItems([...selectedItems, item]);
          setFilters((prevFilters) => {
            const updatedFilters = { ...prevFilters };
            Object.keys(updatedFilters).map((key) => {
              updatedFilters[key].isFocus = false;
            });
            if (((updatedFilters[firstLower(aggregation.name)] || []).ids || []).includes(id)) {
            } else {
              (updatedFilters[firstLower(aggregation.name)] || {}).ids = [...(updatedFilters[firstLower(aggregation.name)].ids || []), id];
              (updatedFilters[firstLower(aggregation.name)] || {}).names = [...(updatedFilters[firstLower(aggregation.name)].names || []), name];
            }
            updatedFilters[firstLower(aggregation.name)].isFocus = true;
            return updatedFilters;
          });
        } catch (error) {
          console.log(`Log error ${error}`);
        }
      };

      const removeSelectedItem = (item: Option) => {
        try {
          const { id, name } = item;
          setSelectedItems(selectedItems.filter((i) => i.id !== item.id));
          setItems([...items, item]);
          setFilters((prevFilters) => {
            const updatedFilters = { ...prevFilters };
            Object.keys(updatedFilters).map((key) => {
              updatedFilters[key].isFocus = false;
            });
            if (((updatedFilters[firstLower(aggregation.name)] || []).ids || []).includes(id)) {
              const aggregationName = firstLower(aggregation.name);
              const filterNames = (updatedFilters[aggregationName]?.names || []).slice();
              const filterIds = (updatedFilters[aggregationName]?.ids || []).slice();
              const index = filterIds.findIndex((filterId: number) => filterId === id);

              if (index !== -1) {
                filterIds.splice(index, 1);
                filterNames.splice(index, 1);

                updatedFilters[aggregationName] = {
                  ...updatedFilters[aggregationName],
                  ids: filterIds,
                  names: filterNames,
                  isFocus: true
                };
              }
            }
            return updatedFilters;
          });
        } catch (error) {

        }
      };

      const searchOptions = async (value: string) => {
        setHasSearched(true);
        if (value.length < 3) {
          setSearchedItems([]);
          return;
        }
        setIsLoading(true);
        const postParameter = { ...responseDataModel, ...filters };
        postParameter && (postParameter.searchText = searchTerm);
        delete postParameter.master;
        const response = await AxiosInstance.post(aggregation.postURL, postParameter);
        if (response.status === 200) {
          setSearchedItems(response.data);
        }
        setIsLoading(false);
      };

      useEffect(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, []);

      const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
      };

      const handleSearchInputClear = () => {
        setSearchTerm('');
        setSearchedItems([]);
      };

      const handleSearchInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      };

      const expanded = expandedSections[aggregation.name] || false;

      return (
        <div className={`multi-select-group accordion__section ${expanded ? 'is-showing show  bg-white' : ''}`}>
          <button className="accordion__head" onClick={() => toggleExpand(aggregation.name)}>
            <h3>{aggregation.labelName}</h3>
            <div className="accordion__icon">
              <img src={iconDownArraow} alt="" />
            </div>
          </button>
          <div className="pad-overwrite accordion__body">
            {selectedItems.length > 0 && (
              <div className="selected-items">
                {selectedItems.map((item) => (
                  <div key={item.id} className="selected-item">
                    {item.name}
                    <button onClick={() => removeSelectedItem(item)}><Icon name="times" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="search-input-container">
              <input
                type="text"
                className="search-input"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchInputKeyPress}
                ref={searchInputRef}
                placeholder="Search..."
              />
              {searchTerm && (
                <button className="search-input-clear" onClick={handleSearchInputClear}>
                  <Icon name="times" />
                </button>
              )}
            </div>

            <div className="search-result-items" style={displayItems.length === 0 ? { padding: 0 } : { padding: '0 10px' }}>
              {isLoading ? (
                <div style={{ padding: 10 }}>Loading...</div>
              ) : !isLoading && searchTerm && searchTerm.length > 2 && displayItems.length === 0 ? (
                <div className="no-items" style={{ padding: 10 }}>No items found</div>
              ) : (
                displayItems.map((item) => (
                  <div key={item.id} className="item" onClick={() => handleItemClick(item)}>
                    {item.name} ({item.count})
                  </div>
                ))
              )}
              {searchedItems.length > 5 && (
                <div className="show-more cursor-pointer" onClick={() => setShowMore(!showMore)}>
                  {showMore ? "Show less" : "Show more"}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const DateRangeGroup: React.FC<DateRangeGroupProps> = ({ aggregation, setExpandedSections }) => {
      const savedFromdate = (filters || {})[firstLower(aggregation.name)]?.fromDate;
      const savedTodate = (filters || {})[firstLower(aggregation.name)]?.toDate;
      const dateRangeGroupRef = useRef<HTMLDivElement>(null);
      const [fromDate, setFromDate] = useState<Date | null>(savedFromdate ? new Date(savedFromdate) : null);
      const [toDate, setToDate] = useState<Date | null>(savedTodate ? new Date(savedTodate) : null);

      useEffect(() => {
        const container = dateRangeGroupRef.current;
        if (!container) return;

        const handleLabelClick = (event: MouseEvent) => {
          event.stopPropagation();
        };

        const input = container.querySelectorAll('input');
        input.forEach((input) => {
          input.addEventListener('click', handleLabelClick);
        });

        return () => {
          input.forEach((input) => {
            input.removeEventListener('click', handleLabelClick);
          });
        };
      }, [aggregation]);

      const toggleExpand = (name: string) => {
        setExpandedSections((prevExpandedSections) => ({
          ...prevExpandedSections,
          [name]: !prevExpandedSections[name],
        }));
      };

      const handleDateChange = (date: Date | null, property: string) => {
        setFilters((prevFilters) => {
          const updatedFilters = { ...prevFilters };
          (updatedFilters[firstLower(aggregation.name)] || {})[property] = date ? moment(date).format("YYYY-MM-DDTHH:mm:ss") : null;
          return updatedFilters;
        });
      }

      const expanded = expandedSections[aggregation.name] || false;

      return (
        <div className={`multi-select-group accordion__section ${expanded ? "is-showing show bg-white" : ""}`}>
          <button className="accordion__head" onClick={() => toggleExpand(aggregation.name)}>
            <h3>{aggregation.labelName}</h3>
            <div className="accordion__icon">
              <img src={iconDownArraow} alt="" />
            </div>
          </button>
          <div className="accordion__body">
            <div className="date-range-inputs">
              <div className="date-picker-container">
                <label>From</label>
                <SemanticDatepicker
                  id="fromDate"
                  format={'MM/DD/YYYY'}
                  onChange={(event, date: any) => {
                    setFromDate(date.value);
                    handleDateChange(date.value, 'fromDate');
                  }}
                  value={fromDate ? new Date(fromDate) : undefined}
                  pointing="top left"
                />
              </div>
              <div className="date-picker-container mt-1">
                <label>To</label>
                <SemanticDatepicker
                  id="toDate"
                  format={'MM/DD/YYYY'}
                  onChange={(event, date: any) => {
                    setFromDate(date.value);
                    handleDateChange(date.value, 'toDate');
                  }}
                  value={toDate ? new Date(toDate) : undefined}
                  pointing="top left"
                />
              </div>
            </div>
          </div>
        </div>
      );
    };


    switch (aggregation.controlType) {
      case 'multi-select-group':
        return <MultiSelectGroup key={aggregation.name} aggregation={aggregation}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        />;
      case 'checkbox-group':
        return <CheckboxGroup key={aggregation.name} aggregation={aggregation}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        />;
      case 'date-range-group':
        return <DateRangeGroup key={aggregation.name} aggregation={aggregation}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
        />
      default:
        return null;
    }
  };

  return (
    <div className='accordion js-accordion'>
      {(aggregations || []).map((aggregation, index) => (
        <div key={index} style={{ marginTop: '12px' }}>
          {renderControl(aggregation)}
        </div>
      ))}
    </div>
  );
};

export interface Option {
  id: number;
  name: string;
  count: number;
}

export default DataGridFilters;